import React, { useEffect, useMemo, useRef, useState } from "react";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import {
  checkStatusCodeSuccess,
  currentDate,
  grnFileValidation,
  notFound,
} from "Components/constants/Common";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { useFormik } from "formik";
import { submitLabel } from "Components/constants/Role";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import Loader from "Components/Base/BaseLoader";
import * as yup from "yup";
import { AddGRNTitle } from "Components/constants/PurchaseOrder";
import {
  addGRNHistory,
  createPOGRN,
  viewPurchaseOrderWORO,
} from "Api/PurchaseOrder";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Upload } from "lucide-react";
import { fileUpload } from "Api/common";
import { getViewPO } from "Api/RoPurchaseOrder";
import {
  GRN_MODULE_LABELS,
  HEADER_TEXT_LABEL,
  INVOICE_ATTACH,
  ITEM_CODE_LABEL,
  MATERIAL_DESC_LABEL,
  MATERIAL_GRP_LABEL,
  PENDING_QTY_LABEL,
  PROJECT_NAME_LABEL,
  RECEIVED_QTY_CANNOT_EXCEED_PENDING_QTY,
  RECEIVED_QTY_LABEL,
  RECIEVED_QTY_MUST_BE_GREATER_ZERO,
  REQUESTED_QTY_LABEL,
  SR_NO_LABEL,
  UOM_LABEL,
  UPLOAD_LABEL,
  VENDOR_ID_LABEL,
  VENDOR_NAME_LABEL,
  VIEW_FILE_LABEL,
} from "Components/constants/ROModule";
import { listOfProject } from "Api/PartRequisition";

const FileUploadRenderer = (props: any) => {
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const fileError = grnFileValidation(file);
    if (fileError) {
      toast.error(fileError);
      return;
    }
    const payload = new FormData();
    payload.append("files", file);

    try {
      const resp = await fileUpload(payload);
      if (checkStatusCodeSuccess(resp?.statusCode)) {
        props.node.setDataValue("file_url", resp?.data[0]);
      } else {
        toast.error(resp?.message);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message);
    }
  };

  const uniqueInputId = `file-upload-${props.rowIndex}-${props.data.id}`;

  const getFullImageUrl = (path: string) => {
    if (!path) return "";
    return `${process.env.REACT_APP_BASEIMAGEURL}${path}`;
  };

  return (
    <div className="d-flex align-items-center gap-2 mt-1">
      <input
        type="file"
        onChange={handleFileChange}
        style={{ display: "none" }}
        id={uniqueInputId}
        accept=".pdf,.jpg,.jpeg,.png"
      />
      <label
        htmlFor={uniqueInputId}
        className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
        style={{ cursor: "pointer" }}
      >
        <Upload size={16} />
        {UPLOAD_LABEL}
      </label>
      {props.data.file_url && (
        <a
          href={getFullImageUrl(props.data.file_url)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary text-decoration-none"
        >
          {VIEW_FILE_LABEL}
        </a>
      )}
    </div>
  );
};

const AddGRN = () => {
  const [loader, setLoader] = useState<boolean>(false);
  const [PID, setPID] = useState<any[]>([]);
  const [projectNames, setProjectNames] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact<any>>(null);
  const [btnLoader, setBtnLoader] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isRoGrn = location.pathname.includes("add-ro-grn");

  const validationSchema = yup.object({
    invoiceDate: yup
      .date()
      .required(validationMessages.required(GRN_MODULE_LABELS.INVOICE_DATE)),
    invoiceNo: yup
      .string()
      .required(validationMessages.required(GRN_MODULE_LABELS.INVOICE_NUMBER)),
    header_text: yup
      .string()
      .required(validationMessages.required(GRN_MODULE_LABELS.HEADER_TEXT)),
    gateEntryNo: yup
      .string()
      .required(validationMessages.required(GRN_MODULE_LABELS.GATE_ENTRY_NO)),
    dmrNo: yup
      .string()
      .required(validationMessages.required(GRN_MODULE_LABELS.DMR_NO)),
    transporterName: yup
      .string()
      .required(
        validationMessages.required(GRN_MODULE_LABELS.TRANSPORTER_NAME)
      ),
    vehicleNo: yup
      .string()
      .required(validationMessages.required(GRN_MODULE_LABELS.VEHICLE_NO)),
    llrNo: yup
      .string()
      .required(validationMessages.required(GRN_MODULE_LABELS.LLR_NO)),
    receiptBy: yup
      .string()
      .required(validationMessages.required(GRN_MODULE_LABELS.RECEIPT_BY)),
  });

  const formik = useFormik({
    initialValues: {
      SelectRONo: "",
      header_text: "",
      gateEntryNo: "",
      dmrNo: "",
      transporterName: "",
      vehicleNo: "",
      llrNo: "",
      receiptBy: "",
      v_id: "",
      vendor_id: "",
      user_draft_id: "",
      invoiceNo: "",
      invoiceDate: new Date().toISOString().split("T")[0],
      recievedDate: new Date().toISOString().split("T")[0],
      gateEntryDate: new Date().toISOString().split("T")[0],
      dmrDate: new Date().toISOString().split("T")[0],
      project_name: "",
    },
    validationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const handleSubmit = (values: any) => {
    const currentGridData = gridRef.current?.api.getDisplayedRowCount()
      ? Array.from(
          { length: gridRef.current.api.getDisplayedRowCount() },
          (_, index) => {
            const rowNode = gridRef.current?.api.getDisplayedRowAtIndex(index);
            return rowNode?.data;
          }
        ).filter(Boolean)
      : [];

    const isAnyReceived = currentGridData.some(
      (item: any) => item.received_quantity > 0
    );

    if (!isAnyReceived) {
      toast.error(RECIEVED_QTY_MUST_BE_GREATER_ZERO);
      return;
    }
    const grnPayload = {
      grnHistoryDetails: PID.map((item: any) => ({
        product_id: item.product_id,
        date_of_issue: values?.invoiceDate,
        received_quantity: item.received_quantity,
        pending_quantity: item.pending_quantity,
      })),
    };

    const payload = {
      invoice_date: values.invoiceDate,
      invoice_number: values.header_text,
      received_date: values.recievedDate,
      header_text: values.header_text,
      gate_entry_no: values.gateEntryNo,
      gate_entry_date: values.gateEntryDate,
      dmr_no: values.dmrNo,
      dmr_date: values.dmrDate,
      transporter_name: values.transporterName,
      vehicle_no: values.vehicleNo,
      llr_no: values.llrNo,
      receipt_by: values.receiptBy,
      vendor_id: values.v_id,
      user_draft_id: values.user_draft_id,
      purchaseOrderItem: currentGridData.map((item: any) => ({
        product_id: item.product_id,
        item_code: item.item_code,
        uom: item.uom,
        received_quantity: item.received_quantity,
        request_quantity: item.qty,
        pending_quantity: item.pending_quantity,
        material_group: item.material_group,
        material_type: item.material_type,
        grn_img: item.file_url || "",
        product_variant_id: item.product_variant_id || null,
      })),
    };

    setBtnLoader(true);
    createPOGRN(Number(id), payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          addGRNHistory(Number(id), grnPayload)
            .then((response) => {
              if (checkStatusCodeSuccess(response?.statusCode)) {
                isRoGrn
                  ? navigate("/RoPurchaseOrder")
                  : navigate("/purchase-order");
              }
            })
            .catch((err) => {
              return err;
            })
            .finally(() => {
              setBtnLoader(false);
            });
          toast.success(res?.message);
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setBtnLoader(false);
      });
  };

  useEffect(() => {
    if (id) {
      setLoader(true);
      setPID([]);

      isRoGrn
        ? getViewPO(Number(id))
            .then((res) => {
              if (checkStatusCodeSuccess(res?.statusCode)) {
                const data = res?.data || {};

                formik.setValues({
                  SelectRONo: data.po_no || "",
                  header_text: "",
                  gateEntryNo: "",
                  dmrNo: "",
                  transporterName: "",
                  vehicleNo: "",
                  llrNo: "",
                  receiptBy: "",
                  v_id: data.vendor.id || "",
                  vendor_id: data.vendor.name || "",
                  user_draft_id: data.user_draft_id || "",
                  invoiceNo: "",
                  invoiceDate: currentDate,
                  recievedDate: currentDate,
                  gateEntryDate: currentDate,
                  dmrDate: currentDate,
                  project_name: "",
                });

                const pidData = data.repairOrderPurchaseOrderItem.map(
                  (item: any) => ({
                    id: item.id,
                    item_code: item.product?.product_code,
                    material_description:
                      item.partRequisitionItem.material_group_description,
                    product_id: item.product_id,
                    uom: item.partRequisitionItem.uom,
                    qty: item?.required_quantity,
                    received_quantity: 0,
                    request_quantity: item.required_quantity || 0,
                    material_group: item.partRequisitionItem.material_group,
                    material_type: item.partRequisitionItem.material_type,
                    pending_quantity: item.pending_quantity,
                    product_variant_id: item.productVariant?.id || null,
                    project_name: item.project?.project_name || "",
                  })
                );

                setPID(pidData);
              } else {
                toast.error(res.message);
              }
            })
            .catch((err) => {
              toast.error(err?.response?.data?.message || err?.message);
            })
            .finally(() => {
              setLoader(false);
            })
        : viewPurchaseOrderWORO(Number(id))
            .then((res) => {
              if (checkStatusCodeSuccess(res?.statusCode)) {
                const data = res?.data || {};

                formik.setValues({
                  SelectRONo: data.po_no || "",
                  header_text: "",
                  gateEntryNo: "",
                  dmrNo: "",
                  transporterName: "",
                  vehicleNo: "",
                  llrNo: "",
                  receiptBy: "",
                  v_id: data.vendor.id || "",
                  vendor_id: data.vendor.name || "",
                  user_draft_id: data.user_draft_id || "",
                  invoiceNo: "",
                  invoiceDate: currentDate,
                  recievedDate: currentDate,
                  gateEntryDate: currentDate,
                  dmrDate: currentDate,
                  project_name: "",
                });

                const pidData = data.repairOrderPurchaseOrderItem.map(
                  (item: any) => ({
                    id: item.id,
                    item_code: item.partRequisitionItem.item_code,
                    product_id: item.product_id,
                    material_description:
                      item.partRequisitionItem.material_description ||
                      item.partRequisitionItem.parts_name,
                    uom: item.partRequisitionItem.uom,
                    qty: item.required_quantity,
                    received_quantity: 0,
                    request_quantity: item.required_quantity,
                    material_group: item.partRequisitionItem.material_group,
                    material_type: item.partRequisitionItem.material_type,
                    pending_quantity: item.pending_quantity,
                    product_variant_id: item.productVariant?.id || null,
                    project_name: item.project?.project_name || "",
                  })
                );

                setPID(pidData);
              } else {
                toast.error(res.message);
              }
            })
            .catch((err) => {
              toast.error(err?.response?.data?.message || err?.message);
            })
            .finally(() => {
              setLoader(false);
            });
    }
  }, [id]);

  useEffect(() => {
    listOfProject()
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data?.project;
          setProjectNames(
            data.map((item: any) => ({
              value: item.id,
              label: item.project_name,
            }))
          );
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      });
  }, []);

  const partsIssueColumns = useMemo(
    () => [
      {
        headerName: SR_NO_LABEL,
        headerTooltip: SR_NO_LABEL,
        valueGetter: "node.rowIndex + 1",
        field: "srNo",
        tooltipField: "srNo",
        flex: 1.5,
        minWidth: 150,
        editable: false,
        sortable: true,
      },
      {
        headerName: ITEM_CODE_LABEL,
        headerTooltip: ITEM_CODE_LABEL,
        field: "item_code",
        tooltipField: "item_code",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: MATERIAL_DESC_LABEL,
        headerTooltip: MATERIAL_DESC_LABEL,
        field: "material_description",
        tooltipField: "material_description",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: PROJECT_NAME_LABEL,
        headerTooltip: "Project Name",
        field: "project_name",
        tooltipField: "project_name",
        editable: false,
        sortable: true,
        flex: 1.5,
        minWidth: 150,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: projectNames.map((item) => item.label),
          valueField: "value",
          displayField: "label",
        },
        valueSetter: (params: any) => {
          const selectedProject = projectNames.find(
            (item) => item.label === params.newValue
          );
          if (selectedProject) {
            params.data.project_name = selectedProject.label;
            params.data.project_id = selectedProject.value;
            return true;
          }
          params.data.project_name = "";
          params.data.project_id = null;
          return true;
        },
      },
      {
        headerName: REQUESTED_QTY_LABEL,
        headerTooltip: REQUESTED_QTY_LABEL,
        field: "qty",
        tooltipField: "qty",
        editable: false,
        sortable: true,
        flex: 1.5,
        minWidth: 150,
        cellEditor: "agTextCellEditor",
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
      },
      {
        headerName: RECEIVED_QTY_LABEL,
        headerTooltip: RECEIVED_QTY_LABEL,
        headerClass: "required",
        field: "received_quantity",
        tooltipField: "received_quantity",
        editable: true,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        cellEditor: "agTextCellEditor",
        valueParser: (params: any) => {
          const value = params.newValue;
          if (value > params.data.pending_quantity) {
            toast.error(RECEIVED_QTY_CANNOT_EXCEED_PENDING_QTY);
            return params.oldValue;
          }
          return value ? parseFloat(value) : 0;
        },
        onCellValueChanged: (event: any) => {
          const { oldValue, newValue, node } = event;
          const rowData = node.data;
          const pendingQty = rowData.pending_quantity;

          if (newValue <= pendingQty && newValue !== oldValue) {
            const difference = newValue - oldValue;
            const updatedPendingQty = pendingQty - difference;

            node.setDataValue("pending_quantity", updatedPendingQty);
          }
        },
      },
      {
        headerName: PENDING_QTY_LABEL,
        headerTooltip: PENDING_QTY_LABEL,
        field: "pending_quantity",
        tooltipField: "pending_quantity",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: UOM_LABEL,
        headerTooltip: UOM_LABEL,
        field: "uom",
        flex: 1.5,
        minWidth: 150,
        tooltipField: "uom",
        editable: false,
        sortable: true,
      },
      {
        headerName: MATERIAL_GRP_LABEL,
        headerTooltip: MATERIAL_GRP_LABEL,
        field: "material_group",
        tooltipField: "material_group",
        editable: false,
        sortable: true,
        flex: 1.5,
        minWidth: 150,
      },

      {
        headerName: INVOICE_ATTACH,
        headerTooltip: INVOICE_ATTACH,
        field: "file_url",
        tooltipField: "file_url",
        editable: true,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        cellRenderer: FileUploadRenderer,
      },
    ],
    []
  );

  document.title = AddGRNTitle;

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <form onSubmit={formik.handleSubmit}>
          <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
            <span className="fs-4 d-flex gap-4">{AddGRNTitle}</span>
            <button
              className="btn btn-danger"
              onClick={() =>
                isRoGrn
                  ? navigate("/RoPurchaseOrder")
                  : navigate("/purchase-order")
              }
            >
              <i className="mdi mdi-arrow-left" />
            </button>
          </div>
          <div className="card py-2 px-4 overflow-x-auto">
            <div className="d-flex flex-wrap justify-content-between mb-1 pe-auto">
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="invoiceDate"
                  placeholder={SelectPlaceHolder("")}
                  type="date"
                  label={GRN_MODULE_LABELS.INVOICE_DATE}
                  value={formik.values.invoiceDate}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.invoiceDate}
                  touched={formik.touched.invoiceDate}
                  required
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="invoiceNo"
                  label={GRN_MODULE_LABELS.INVOICE_NUMBER}
                  placeholder={InputPlaceHolder(
                    GRN_MODULE_LABELS.INVOICE_NUMBER
                  )}
                  value={formik.values.invoiceNo}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.invoiceNo}
                  touched={formik.touched.invoiceNo}
                  required
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="vendor_id"
                  label={VENDOR_NAME_LABEL}
                  placeholder={InputPlaceHolder(VENDOR_NAME_LABEL)}
                  value={formik.values.vendor_id}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.vendor_id}
                  touched={formik.touched.vendor_id}
                  disabled
                />
              </div>

              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="user_draft_id"
                  label={VENDOR_ID_LABEL}
                  placeholder={InputPlaceHolder(VENDOR_ID_LABEL)}
                  value={formik.values.user_draft_id}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.user_draft_id}
                  touched={formik.touched.user_draft_id}
                  disabled
                />
              </div>

              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="recievedDate"
                  placeholder={SelectPlaceHolder("")}
                  type="date"
                  label={GRN_MODULE_LABELS.RECEIVED_DATE}
                  value={formik.values.recievedDate}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.recievedDate}
                  touched={formik.touched.recievedDate}
                  required
                />
              </div>
            </div>
            <div className="d-flex flex-wrap justify-content-between mb-1 pe-auto">
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="header_text"
                  label={HEADER_TEXT_LABEL}
                  placeholder={InputPlaceHolder(HEADER_TEXT_LABEL)}
                  value={formik.values.header_text}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.header_text}
                  touched={formik.touched.header_text}
                  required
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="gateEntryNo"
                  label={GRN_MODULE_LABELS.GATE_ENTRY_NO}
                  placeholder={InputPlaceHolder(
                    GRN_MODULE_LABELS.GATE_ENTRY_NO
                  )}
                  value={formik.values.gateEntryNo}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.gateEntryNo}
                  touched={formik.touched.gateEntryNo}
                  required
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="gateEntryDate"
                  type="date"
                  label={GRN_MODULE_LABELS.GATE_ENTRY_DATE}
                  value={formik.values.gateEntryDate}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.gateEntryDate}
                  touched={formik.touched.gateEntryDate}
                  required
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="dmrNo"
                  label={GRN_MODULE_LABELS.DMR_NO}
                  placeholder={InputPlaceHolder(GRN_MODULE_LABELS.DMR_NO)}
                  value={formik.values.dmrNo}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.dmrNo}
                  touched={formik.touched.dmrNo}
                  required
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="dmrDate"
                  placeholder={SelectPlaceHolder("")}
                  type="date"
                  label={GRN_MODULE_LABELS.DMR_DATE}
                  value={formik.values.dmrDate}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.dmrDate}
                  touched={formik.touched.dmrDate}
                  required
                />
              </div>
            </div>{" "}
            <div className="d-flex flex-wrap justify-content-between mb-1 pe-auto">
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="transporterName"
                  label={GRN_MODULE_LABELS.TRANSPORTER_NAME}
                  placeholder={InputPlaceHolder(
                    GRN_MODULE_LABELS.TRANSPORTER_NAME
                  )}
                  value={formik.values.transporterName}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.transporterName}
                  touched={formik.touched.transporterName}
                  required
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="vehicleNo"
                  label={GRN_MODULE_LABELS.VEHICLE_NO}
                  placeholder={InputPlaceHolder(GRN_MODULE_LABELS.VEHICLE_NO)}
                  value={formik.values.vehicleNo}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.vehicleNo}
                  touched={formik.touched.vehicleNo}
                  required
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="llrNo"
                  label={GRN_MODULE_LABELS.LLR_NO}
                  placeholder={InputPlaceHolder(GRN_MODULE_LABELS.LLR_NO)}
                  value={formik.values.llrNo}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.llrNo}
                  touched={formik.touched.llrNo}
                  required
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="receiptBy"
                  label={GRN_MODULE_LABELS.RECEIPT_BY}
                  placeholder={InputPlaceHolder(GRN_MODULE_LABELS.RECEIPT_BY)}
                  value={formik.values.receiptBy}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.receiptBy}
                  touched={formik.touched.receiptBy}
                  required
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2"></div>
            </div>
            <hr />
            <div className="table-css">
              <div className="ag-theme-quartz">
                <AgGridReact
                  ref={gridRef}
                  rowData={PID}
                  columnDefs={partsIssueColumns}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                    flex: 1,
                    tooltipComponentParams: {
                      color: "blue",
                    },
                  }}
                  domLayout="normal"
                  getRowHeight={() => 35}
                  overlayNoRowsTemplate={notFound.dataNotFound}
                />
              </div>
            </div>
            <div className="d-flex justify-content-end mb-1 mt-4 w-100">
              <div className="col-12 d-flex justify-content-end mb-0">
                <BaseButton
                  type="submit"
                  label={submitLabel}
                  color="success"
                  loader={btnLoader}
                  disable={btnLoader}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
export default AddGRN;
